
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCartState } from '../state/CartContext';
import { useAuth } from '../state/auth';
import { NotificationCenter } from './NotificationCenter';
import { SearchOverlay } from '../features/search/SearchOverlay';
import { ROUTES } from '../domain/routes';

interface AppHeaderProps {
    title?: string;
    showBack?: boolean;
    backIcon?: string;
    isHome?: boolean;
    transparent?: boolean;
    backTo?: string;
    onBackClick?: () => void;
    rightAction?: React.ReactNode;
    searchMode?: 'products' | 'blog';
}

export const SimpleHeader = React.memo(({ 
    title, 
    showBack = true, 
    backIcon = 'arrow_back',
    isHome = false, 
    transparent = false, 
    backTo, 
    onBackClick,
    rightAction,
    searchMode = 'products'
}: AppHeaderProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { itemCount } = useCartState();
    const { user, isAdmin, isSuperAdmin } = useAuth();
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    
  // Improved Back Logic to prevent loops
  const handleBack = () => {
    if (onBackClick) {
      onBackClick();
      return;
    }
    if (backTo) {
      navigate(backTo, { replace: true });
      return;
    }

    // If we are deep in history, go back once, otherwise go Home
    if (window.history.state && window.history.state.idx > 1) {
      navigate(-1);
    } else {
      navigate(ROUTES.HOME, { replace: true });
    }
  };

    const isBlogPath = location.pathname.startsWith('/blog');
    
    // Use isAdmin/isSuperAdmin from AuthContext (properly checks token claims)
    const showAdminButton = isAdmin || isSuperAdmin;
    const showPartnerButton = !!user?.partnerId;

    return (
        <>
            <SearchOverlay 
                isOpen={isSearchOpen} 
                onClose={() => setIsSearchOpen(false)} 
                mode={searchMode}
            />
            <header className={`sticky top-0 z-[60] w-full transition-all duration-300 ${
                transparent 
                ? 'bg-transparent' 
                : 'bg-white/5 dark:bg-black/20 backdrop-blur-xl border-b border-white/10 dark:border-white/5'
            } px-4 py-3`}>
                <div className="flex items-center justify-between gap-2 max-w-7xl mx-auto">
                    <div className="flex items-center gap-2 min-w-[40px]">
                        {showBack && !isHome && (
                            <button 
                                onClick={handleBack} 
                                className="flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors active:scale-95"
                                aria-label="Volver a la página anterior"
                            >
                                <span className="material-symbols-outlined text-text-main dark:text-white" style={{fontSize: '24px'}}>{backIcon}</span>
                            </button>
                        )}
                        {!isHome && !showBack && (
                            <button
                                onClick={() => navigate(ROUTES.HOME)}
                                className="flex size-10 shrink-0 items-center justify-center rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-colors active:scale-95"
                                title="Ir a Inicio"
                            >
                                <span className="material-symbols-outlined text-text-main dark:text-white" style={{fontSize: '24px'}}>home</span>
                            </button>
                        )}
                        {isHome ? (
                            <div className="flex flex-col">
                                <h2 className="text-text-main dark:text-white text-xl font-black leading-none tracking-tighter">AllTuc</h2>
                                <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-primary">Marketplace</span>
                            </div>
                        ) : (
                            !showBack && <h1 className="text-text-main dark:text-white text-lg font-bold truncate max-w-[120px]">{title}</h1>
                        )}
                    </div>

                    {showBack && !isHome && (
                        <div className="flex-1 flex justify-center overflow-hidden px-2">
                            <h1 className="text-text-main dark:text-white text-sm font-black uppercase tracking-widest truncate">
                                {title}
                            </h1>
                        </div>
                    )}

<div className="flex items-center justify-end gap-1 sm:gap-2">
            {rightAction}
            
  {!user && (isHome || isBlogPath) && !rightAction && (
    <button
      onClick={() => navigate(ROUTES.LOGIN, { state: { from: location.pathname } })}
      className="mr-1 flex items-center gap-2 px-4 py-2 bg-text-main dark:bg-white text-white dark:text-black rounded-xl text-[10px] font-black uppercase tracking-wider shadow-xl shadow-black/10 transition-transform active:scale-95"
      title="Iniciar sesión en tu cuenta"
    >
      <span className="material-symbols-outlined text-sm">login</span>
      <span className="hidden xs:inline">Ingresar</span>
    </button>
  )}
            

            
  {showPartnerButton && (isHome || isBlogPath) && (
    <div className="relative group">
      <button
        onClick={() => navigate(ROUTES.PARTNER.DASHBOARD)}
        className="flex items-center gap-1.5 px-3 py-2 bg-orange-500 text-white rounded-xl text-[10px] font-black uppercase tracking-wider shadow-xl shadow-orange-500/20 transition-all hover:bg-orange-600 active:scale-95"
        title="Ir a mi tienda - Panel de vendedor"
      >
        <span className="material-symbols-outlined text-sm">storefront</span>
        <span className="hidden xs:inline">Tienda</span>
      </button>
      <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[8px] text-gray-500 dark:text-gray-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        Panel de vendedor
      </span>
    </div>
  )}
            
            {!isBlogPath && isHome && (
    <div className="relative group">
      <button
        onClick={() => navigate('/blog')}
        className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-all active:scale-95 border border-purple-200 dark:border-purple-500/20"
        title="Blog de AllTuc - Noticias y artículos"
      >
        <span className="material-symbols-outlined text-sm filled">article</span>
        <span className="text-[9px] font-black lowercase tracking-tighter">Blog</span>
      </button>
    </div>
  )}

  {isHome && (
    <div className="relative group">
      <button
        onClick={() => navigate('/planes')}
        className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50 transition-all active:scale-95 border border-green-200 dark:border-green-500/20"
        title="Ver planes para vendedores - Empieza a vender"
      >
        <span className="material-symbols-outlined text-sm">workspace_premium</span>
        <span className="text-[9px] font-black uppercase tracking-tighter">Vender</span>
      </button>
    </div>
  )}

  {isHome && (
    <div className="relative group">
      <button
        onClick={() => navigate('/tienda/alltucoficial')}
        className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl bg-primary/10 text-primary hover:bg-primary/20 transition-all active:scale-95 border border-primary/20"
        title="Tienda oficial AllTuc - Productos con garantía"
      >
        <span className="material-symbols-outlined text-sm filled">verified</span>
        <span className="text-[9px] font-black uppercase tracking-tighter">AllTuc</span>
      </button>
    </div>
  )}
            
  <div className="relative group">
    <button 
      onClick={() => setIsSearchOpen(true)} 
      className="flex items-center justify-center rounded-full h-10 w-10 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
      title="Buscar productos"
      aria-label="Abrir búsqueda de productos"
    >
      <span className="material-symbols-outlined text-text-main dark:text-white" style={{fontSize: '22px'}}>search</span>
    </button>
    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[8px] text-gray-500 dark:text-gray-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
      Buscar
    </span>
  </div>
  
  <div className="relative group">
    <div title="Notificaciones">
      <NotificationCenter />
    </div>
    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[8px] text-gray-500 dark:text-gray-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
      Alertas
    </span>
  </div>
  
  <div className="relative group">
    <button 
      onClick={() => navigate('/cart')} 
      className="flex items-center justify-center rounded-full h-10 w-10 hover:bg-black/5 dark:hover:bg-white/10 transition-colors relative"
      title={`Carrito de compras${itemCount > 0 ? ` (${itemCount} items)` : ''}`}
    >
      <span className="material-symbols-outlined text-text-main dark:text-white" style={{fontSize: '22px'}}>shopping_cart</span>
      {itemCount > 0 && (
        <span className="absolute top-1 right-1 w-4 h-4 bg-primary text-white text-[9px] font-black rounded-full flex items-center justify-center border-2 border-white dark:border-black">
          {itemCount}
        </span>
      )}
    </button>
    <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[8px] text-gray-500 dark:text-gray-400 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
      {itemCount > 0 ? `${itemCount} items` : 'Carrito'}
    </span>
  </div>
          </div>
                </div>
            </header>
        </>
    );
});

SimpleHeader.displayName = 'SimpleHeader';
